import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { PublicPlan } from '../../services/billing';
import { hoverLift } from './landingStyles';

function money(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function priceLine(plan: PublicPlan) {
  if (plan.comingLater) return 'Coming later';
  const monthly = plan.prices.find((price) => price.billingInterval === 'month' && price.unitAmountCents != null);
  if (monthly && monthly.unitAmountCents != null) return `${money(monthly.unitAmountCents)}/month`;
  if (plan.planKey === 'free') return '$0';
  return '';
}

type Props = {
  plans: PublicPlan[];
};

const PricingCards: React.FC<Props> = ({ plans }) => {
  if (!plans.length) return null;
  return (
    <Box component="section" id="pricing" aria-labelledby="pricing-heading">
      <Typography id="pricing-heading" variant="h2" sx={{ fontWeight: 800, fontSize: { xs: 32, md: 44 }, mb: 1 }}>
        Simple pricing
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Pro includes 10× the monthly AI reviews and automatic comp searches on the Free plan. It is not a promise
        about profit or a recommendation to buy.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {plans.map((plan) => {
          const later = plan.comingLater;
          return (
            <Paper
              key={plan.planKey}
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderTopWidth: 4,
                borderTopStyle: 'solid',
                borderTopColor: 'brand.yellow',
                ...hoverLift,
              }}
            >
              <Typography variant="h3" sx={{ fontSize: 28, fontWeight: 800, mb: 1 }}>
                {later ? 'Teams — Coming later' : plan.displayName}
              </Typography>
              {!later && (
                <Typography sx={{ fontSize: 28, fontWeight: 800, mb: 2 }}>{priceLine(plan)}</Typography>
              )}
              {later ? (
                <Typography color="text.secondary">
                  Shared workspaces are not available yet. No price is set.
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                  <li>
                    {plan.aiReviewsIncluded == null
                      ? 'AI review allowance is set with the plan'
                      : `${plan.aiReviewsIncluded} AI reviews each period`}
                  </li>
                  <li>
                    {plan.compsSearchesIncluded == null
                      ? 'Comp search allowance is set with the plan'
                      : `${plan.compsSearchesIncluded} automatic comp searches each period`}
                  </li>
                  {plan.creditsEnabled && <li>Request credits after the included allowance</li>}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default PricingCards;
